// CRM Store using Zustand with API Integration
import axios from 'axios';
import { getCurrentDate, getCurrentDateTime } from '../utils/date';
import dayjs from 'dayjs';

import { create } from 'zustand';
import { Company, Contact, Customer, Opportunity, Task, User, EMPTY_USER, Project, Schedule, ScheduleCategory, ScheduleParticipant, Contract, ReceivablePlan, Invoice, Expense, Loan, Payable, FundRecord, FinanceConfig, PaymentRecord, MonthlyExpenseRecord, Supplier } from '../types';
import { parseScheduleContentPayload, buildScheduleContentPayload } from '../utils/scheduleSubtype';
import * as services from '../services';
import { companyService } from '../services/modules/crm/companyService';
import { assertResultOk, getAxiosBody, unwrapListBody, unwrapResultPageWithTotal } from '../utils/axiosResult';
import { message } from '../utils/message';
import { logger } from '../utils/logger';
import { normalizeContractFinToUi, normalizeProjectFromApi, normalizeSupplierFromApi } from '../utils/crmDtoNormalize';
import type { Result } from '../services/types';
import { fetchCurrentUserFromApi, fetchUsersFromApi } from '../services/user';
import { hasAuthToken } from '../utils/authToken';
import { toExpenseSavePayload } from '../services/modules/finance/expensePayload';
import { toMonthlyExpenseCreatePayload } from '../services/modules/finance/monthlyExpensePayload';
import { buildSafePageParams } from '../utils/pagination';
import { isValidEntityId, uniqueValidEntityIds } from '../utils/entityId';
import { usePermissionStore } from './permissionStore';
import { createSingleFlight, shouldLoadDashboardCoreUsers } from '../pages/dashboard/dashboardDataPolicy';

import {
  mapFinanceConfigFromApi,
  mapReceivableVoToPlan,
  mapReceivablePlanToApiPayload,
  normalizePayableRow,
  mapPayableVoToPayable,
  mapLoanVoToLoan,
  mapInvoiceVoToInvoice,
  normalizeOpportunityFromApi,
  mapApiTaskRecordToTask,
  taskToSaveParams,
  syncProjectTaskAssigneeSchedules,
  replaceDerivedSchedulesForProjectTask,
  supplierToSavePayload,
  handleApiError,
} from './crmDataMappers';
import type { ScheduleSyncGetter } from './crmDataMappers';

function skipFetchIfNoToken(label: string): boolean {
  if (!hasAuthToken()) {
    logger.debug(`${label}: skip（无 token）`);
    return true;
  }
  return false;
}

/** 分页拉全量时按 id 去重（后端 create_time 相同会导致 OFFSET 分页重复行） */
function dedupeById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of items) {
    const id = String(item.id);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(item);
  }
  return out;
}






// Loading状态类型
interface LoadingState {
  companies: boolean;
  contacts: boolean;
  opportunities: boolean;
  projects: boolean;
  tasks: boolean;
  schedules: boolean;
  finance: boolean;
  users: boolean;
}

interface CRMStore {
  // User state
  currentUser: User;
  users: User[];

  // Data state
  companies: Company[];
  companyTabTotal: number;
  contacts: Contact[];
  customers: Customer[];
  opportunities: Opportunity[];
  tasks: Task[];
  projects: Project[];
  schedules: Schedule[];
  salesStats: { totalSales: number; monthlyGrowth: number; newCustomers: number; customerGrowth: number; pipelineValue: number; taskCompletionRate: number; };

  // 财务模块数据
  contracts: Contract[];
  contractsLoaded: boolean;
  receivablePlans: ReceivablePlan[];
  paymentRecords: PaymentRecord[];
  paymentRecordsLoaded: boolean;
  invoices: Invoice[];
  expenses: Expense[];
  loans: Loan[];
  payables: Payable[];
  fundRecords: FundRecord[];
  financeConfig: FinanceConfig;
  // 企业月结费用
  monthlyExpenses: MonthlyExpenseRecord[];
  // 供应商管理
  suppliers: Supplier[];

  // UI state
  sidebarCollapsed: boolean;
  loading: LoadingState;

  // Actions
  setCurrentUser: (user: User) => void;
  toggleSidebar: () => void;

  // 初始化数据 - 从后端加载或使用模拟数据
  initializeData: () => Promise<void>;
  
  // 按需加载业务数据 - 性能优化
  loadBusinessData: (modules: string[]) => Promise<void>;
  /** 客户详情页：只拉当前客户维度的数据，避免全量分页把后端/浏览器拖死 */
  loadCompanyDetailBusinessData: (companyId: string) => Promise<void>;

  // Company actions
  fetchCompanies: () => Promise<void>;
  addCompany: (company: Company) => Promise<void>;
  updateCompany: (id: string, company: Partial<Company>) => Promise<void>;
  deleteCompany: (id: string) => Promise<void>;

  // Contact actions
  fetchContacts: () => Promise<void>;
  addContact: (contact: Contact) => Promise<void>;
  updateContact: (id: string, contact: Partial<Contact>) => Promise<void>;
  deleteContact: (id: string) => Promise<void>;
  getContactsByCompany: (companyId: string) => Contact[];

  // Customer actions
  addCustomer: (customer: Customer) => Promise<void>;
  updateCustomer: (id: string, customer: Partial<Customer>) => Promise<void>;
  deleteCustomer: (id: string) => Promise<void>;

  // Opportunity actions
  fetchOpportunities: () => Promise<void>;
  addOpportunity: (opportunity: Opportunity) => Promise<void>;
  updateOpportunity: (id: string, opportunity: Partial<Opportunity>) => Promise<void>;
  deleteOpportunity: (id: string) => Promise<void>;

  // Task actions
  fetchTasks: (opts?: { maxPages?: number }) => Promise<void>;
  addTask: (task: Task, options?: { quiet?: boolean; skipAssigneeScheduleSync?: boolean }) => Promise<number>;
  updateTask: (id: string, task: Partial<Task>) => Promise<number>;
  deleteTask: (id: string) => Promise<void>;
  completeTask: (id: string) => Promise<void>;
  reopenTask: (id: string) => Promise<void>;

  // Project actions
  fetchProjects: () => Promise<void>;
  /** 拉取单个项目详情（含 attachments 等多版本附件） */
  fetchProjectById: (id: string) => Promise<void>;
  addProject: (project: Project) => Promise<void>;
  updateProject: (id: string, project: Partial<Project>) => Promise<void>;
  /** 仅更新进度：调用 PUT .../progress，避免 PUT /projects/:id 触发 DTO 全文校验 400 */
  updateProjectProgressOnly: (id: string, progress: number) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;

  // Schedule actions
  fetchSchedules: () => Promise<void>;
  addSchedule: (schedule: Schedule, options?: { quiet?: boolean; skipFetch?: boolean }) => Promise<string | null>;
  updateSchedule: (id: string, schedule: Partial<Schedule>) => Promise<boolean>;
  deleteSchedule: (id: string, options?: { quiet?: boolean }) => Promise<void>;
  normalizeSchedulePayload: (raw: Record<string, unknown>) => Record<string, unknown>;
  mapScheduleTypeToCategory: (schedule: Schedule) => Schedule;

  // Contract actions
  fetchContracts: () => Promise<void>;
  addContract: (contract: Contract) => Promise<void>;
  updateContract: (id: string, contract: Partial<Contract>) => Promise<void>;
  deleteContract: (id: string) => Promise<void>;

  // ReceivablePlan actions
  fetchReceivablePlans: () => Promise<void>;
  addReceivablePlan: (plan: ReceivablePlan) => Promise<void>;
  updateReceivablePlan: (id: string, plan: Partial<ReceivablePlan>) => Promise<void>;
  deleteReceivablePlan: (id: string) => Promise<void>;

  // PaymentRecord actions
  fetchPaymentRecords: () => Promise<void>;
  addPaymentRecord: (record: PaymentRecord) => Promise<boolean>;
  updatePaymentRecord: (id: string, record: Partial<PaymentRecord>) => Promise<void>;
  deletePaymentRecord: (id: string) => Promise<void>;

  // Invoice actions
  fetchInvoices: () => Promise<void>;
  addInvoice: (invoice: Invoice) => Promise<void>;
  updateInvoice: (id: string, invoice: Partial<Invoice>) => Promise<void>;
  deleteInvoice: (id: string) => Promise<void>;

  // Expense actions
  fetchExpenses: (opts?: { maxPages?: number; pageSize?: number }) => Promise<void>;
  addExpense: (expense: Expense | Record<string, unknown>) => Promise<boolean>;
  updateExpense: (id: string, expense: Partial<Expense> | Record<string, unknown>) => Promise<boolean>;
  deleteExpense: (id: string) => Promise<void>;

  // Loan actions
  fetchLoans: () => Promise<void>;
  addLoan: (loan: Loan) => Promise<void>;
  updateLoan: (id: string, loan: Partial<Loan>) => Promise<void>;
  deleteLoan: (id: string) => Promise<void>;

  // Payable actions
  fetchPayables: () => Promise<void>;
  addPayable: (payable: Payable) => Promise<void>;
  updatePayable: (id: string, payable: Partial<Payable>) => Promise<void>;
  confirmPayable: (id: string, payload: { version: number; amount: number; paidDate?: string; paymentMethod?: string; remark?: string }) => Promise<boolean>;
  deletePayable: (id: string) => Promise<void>;

  // FundRecord actions
  fetchFundRecords: () => Promise<void>;
  addFundRecord: (record: FundRecord) => Promise<void>;
  updateFundRecord: (id: string, record: Partial<FundRecord>) => Promise<void>;
  deleteFundRecord: (id: string) => Promise<void>;

  // FinanceConfig actions
  fetchFinanceConfig: () => Promise<void>;
  updateFinanceConfig: (config: Partial<FinanceConfig>) => Promise<void>;

  // 用户管理
  fetchUsers: () => Promise<void>;

  // 按客户筛选数据的 selectors
  getExpensesByCompany: (companyId: string) => Expense[];
  getInvoicesByCompany: (companyId: string) => Invoice[];
  getContractsByCompany: (companyId: string) => Contract[];
  getProjectsByCompany: (companyId: string) => Project[];
  getOpportunitiesByCompany: (companyId: string) => Opportunity[];

  // 月结费用 actions
  fetchMonthlyExpenses: (params?: Record<string, unknown>) => Promise<void>;
  addMonthlyExpense: (expense: MonthlyExpenseRecord, silent?: boolean) => Promise<void>;
  updateMonthlyExpense: (id: string, expense: Partial<MonthlyExpenseRecord>) => Promise<void>;
  deleteMonthlyExpense: (id: string) => Promise<void>;

  // 供应商管理 actions
  fetchSuppliers: () => Promise<void>;
  addSupplier: (supplier: Supplier) => Promise<void>;
  updateSupplier: (id: string, supplier: Partial<Supplier>) => Promise<void>;
  deleteSupplier: (id: string) => Promise<void>;
}



export const useCRMStore = create<CRMStore>((set, get) => ({
  // Initial state - P0修复: 删除硬编码数据,从API获取
  currentUser: EMPTY_USER,
  users: [], // P0修复: 删除硬编码用户数据,从API获取
  companies: [],
  companyTabTotal: 0,
  contacts: [],
  customers: [],
  opportunities: [],
  tasks: [],
  projects: [],
  schedules: [],
  salesStats: { totalSales: 0, monthlyGrowth: 0, newCustomers: 0, customerGrowth: 0, pipelineValue: 0, taskCompletionRate: 0 },
  // 财务模块数据
  contracts: [],
  contractsLoaded: false,
  receivablePlans: [],
  paymentRecords: [],
  paymentRecordsLoaded: false,
  invoices: [],
  expenses: [],
  loans: [],
  payables: [],
  fundRecords: [],
  financeConfig: { id: '1', incomeCategories: [], expenseCategories: [], defaultTaxRate: 0.06, companyAccounts: [], companyFinanceInfo: [], updatedAt: getCurrentDateTime() },
  monthlyExpenses: [], // P0修复: 删除硬编码数据,从API获取
  suppliers: [],
  sidebarCollapsed: false,
  loading: {
    companies: false,
    contacts: false,
    opportunities: false,
    projects: false,
    tasks: false,
    schedules: false,
    finance: false,
    users: false,
  },

  // User actions
  setCurrentUser: (user) => {
    const safeUser = { ...user } as Record<string, unknown>;
    delete safeUser.password;
    set({ currentUser: safeUser as unknown as User });
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('user', JSON.stringify(safeUser));
      } catch {
        // ignore localStorage write errors (private mode / quota)
      }
    }
  },
  toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

  // 初始化数据 - 性能优化：只加载核心数据，其他数据按需加载
  initializeData: createSingleFlight(async () => {
    logger.debug('initializeData 开始执行');

    if (typeof window !== 'undefined' && !localStorage.getItem('token')) {
      logger.debug('initializeData: skip（无 token）');
      return;
    }
    
    logger.debug('正在从后端加载核心数据...');

    // 性能优化：只加载首屏必需的核心数据
    const coreLoadFunctions = shouldLoadDashboardCoreUsers(
      usePermissionStore.getState().hasPermission,
    )
      ? [{ name: 'fetchUsers', fn: get().fetchUsers }]
      : [];

    // 并发加载核心数据
    await Promise.all(coreLoadFunctions.map(async ({ name, fn }) => {
      try {
        logger.debug(`加载 ${name}...`);
        await fn();
        logger.debug(`${name} 加载成功`);
      } catch (error) {
        logger.error(`${name} 加载失败:`, error);
      }
    }));

    // 尝试获取当前用户（token 必须对应后端有效会话）
    try {
      const user = await fetchCurrentUserFromApi();
      if (user) {
        set({ currentUser: user });
      }
    } catch {
      logger.debug('获取当前用户失败，使用默认用户');
    }

    logger.debug('核心数据初始化完成');
  }),

  // 按需加载业务数据 - 性能优化：进入具体页面时调用
  loadBusinessData: async (modules: string[]) => {
    if (typeof window !== 'undefined' && !localStorage.getItem('token')) {
      logger.debug('loadBusinessData: skip（未登录，无 token）');
      return;
    }

    const moduleLoaders: Record<string, () => Promise<void>> = {
      companies: get().fetchCompanies,
      contacts: get().fetchContacts,
      opportunities: get().fetchOpportunities,
      projects: get().fetchProjects,
      tasks: get().fetchTasks,
      schedules: get().fetchSchedules,
      contracts: get().fetchContracts,
      receivablePlans: get().fetchReceivablePlans,
      paymentRecords: get().fetchPaymentRecords,
      invoices: get().fetchInvoices,
      expenses: get().fetchExpenses,
      loans: get().fetchLoans,
      payables: get().fetchPayables,
      monthlyExpenses: get().fetchMonthlyExpenses,
      fundRecords: get().fetchFundRecords,
      financeConfig: get().fetchFinanceConfig,
    };

    // 顺序加载：避免仪表盘首次进入时 Promise.all 同时打爆后端连接/权限校验导致 401 风暴与 30s 超时
    for (const module of modules) {
      const loader = moduleLoaders[module];
      if (loader) {
        try {
          await loader();
        } catch (error) {
          logger.error(`加载 ${module} 失败:`, error);
        }
      }
    }
  },

  loadCompanyDetailBusinessData: async (companyId: string) => {
    if (!companyId?.trim()) return;
    const cid = companyId.trim();

    const safe = async (label: string, fn: () => Promise<void>) => {
      try {
        await fn();
      } catch (error) {
        logger.error(`客户详情 ${label} 加载失败:`, error);
      }
    };

    await Promise.all([
      safe('contacts', async () => {
        set((state) => ({ loading: { ...state.loading, contacts: true } }));
        try {
          const raw = await services.getContactsByCompany(cid);
          const body = getAxiosBody<unknown>(raw);
          const list = unwrapListBody<Contact>(body);
          set((state) => ({
            contacts: [...state.contacts.filter((c) => c.companyId !== cid), ...list],
          }));
        } catch (error) {
          handleApiError(error, '获取联系人列表失败');
        } finally {
          set((state) => ({ loading: { ...state.loading, contacts: false } }));
        }
      }),
      safe('opportunities', async () => {
        set((state) => ({ loading: { ...state.loading, opportunities: true } }));
        try {
          const raw = await services.getOpportunities({
            pageNum: 1,
            pageSize: 100,
            outcomeCategory: 'ALL',
            companyId: cid,
          });
          const response = getAxiosBody<
            Result<{ records?: Record<string, unknown>[]; total?: number } | Record<string, unknown>[]>
          >(raw);
          if (response?.code === 200 && response.data != null) {
            const d = response.data as { records?: Record<string, unknown>[] } | Record<string, unknown>[];
            const rows = Array.isArray(d) ? d : d.records ?? [];
            const opportunities = rows.map((row) => normalizeOpportunityFromApi(row));
            set((state) => ({
              opportunities: [...state.opportunities.filter((o) => o.companyId !== cid), ...opportunities],
            }));
          }
        } catch (error) {
          handleApiError(error, '获取商机列表失败');
        } finally {
          set((state) => ({ loading: { ...state.loading, opportunities: false } }));
        }
      }),
      safe('projects', async () => {
        set((state) => ({ loading: { ...state.loading, projects: true } }));
        try {
          const response = (await services.getProjects({
            pageNum: 1,
            pageSize: 100,
            companyId: cid,
          })) as Result<{ records?: Project[]; total?: number }>;
          if (response?.code === 200 && response.data) {
            const rec = response.data.records ?? [];
            const mapped = rec.map((r) =>
              normalizeProjectFromApi(r as unknown as Record<string, unknown>)
            );
            set((state) => ({
              projects: [...state.projects.filter((p) => p.companyId !== cid), ...mapped],
            }));
          }
        } catch (error) {
          handleApiError(error, '获取项目列表失败');
        } finally {
          set((state) => ({ loading: { ...state.loading, projects: false } }));
        }
      }),
      safe('contracts', async () => {
        set((state) => ({ loading: { ...state.loading, finance: true } }));
        try {
          const raw = await services.getContracts({
            companyId: cid,
            pageNum: 1,
            pageSize: 100,
          });
          if (raw?.code === 200 && raw.data?.records) {
            const rec = raw.data.records.map((r) =>
              normalizeContractFinToUi(r as unknown as Record<string, unknown>)
            );
            set((state) => ({
              contracts: [...state.contracts.filter((c) => c.companyId !== cid), ...rec],
            }));
            const projectIds = uniqueValidEntityIds(rec.map((c) => c.projectId));
            // 不阻塞客户详情首屏：合同列表已写入 store，项目详情后台补齐即可
            void Promise.all(projectIds.map((pid) => get().fetchProjectById(pid))).catch((e) =>
              logger.error('客户详情：批量拉取关联项目失败', e)
            );
          }
        } catch (error) {
          handleApiError(error, '获取合同列表失败');
        } finally {
          set((state) => ({ loading: { ...state.loading, finance: false } }));
        }
      }),
      safe('invoices', async () => {
        try {
          const raw = await services.getInvoices({
            companyId: cid,
            pageNum: 1,
            pageSize: 100,
          });
          const body = getAxiosBody<unknown>(raw);
          const list = unwrapListBody<Record<string, unknown>>(body).map(mapInvoiceVoToInvoice);
          set((state) => ({
            invoices: [...state.invoices.filter((i) => i.companyId !== cid), ...list],
          }));
        } catch (error) {
          handleApiError(error, '获取发票列表失败');
        }
      }),
    ]);
  },

  // ==================== 用户管理 ====================
  fetchUsers: async () => {
    if (typeof window !== 'undefined' && !localStorage.getItem('token')) {
      logger.debug('fetchUsers: skip（无 token）');
      return;
    }
    try {
      const users = await fetchUsersFromApi();
      set({ users });
      logger.debug('从API获取用户列表成功:', users.length);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        logger.debug('获取员工列表跳过（401，未登录或会话无效）');
        set({ users: [] });
        return;
      }
      logger.error('获取员工列表失败,使用本地数据:', error);
      set({ users: [] });
    }
  },

  // ==================== Company actions ====================
  fetchCompanies: async () => {
    set((state) => ({ loading: { ...state.loading, companies: true } }));
    try {
      const page = await companyService.list({
        pageNum: 1,
        pageSize: 100,
        tab: 'all',
      });
      set((state) => ({ companyTabTotal: typeof page.total === 'number' ? page.total : state.companyTabTotal }));
      set({ companies: [...page.records] });
    } catch (error) {
      handleApiError(error, '获取客户列表失败');
    } finally {
      set((state) => ({ loading: { ...state.loading, companies: false } }));
    }
  },

  addCompany: async (company) => {
    try {
      await companyService.create(company);
      await get().fetchCompanies();
      message.success('客户创建成功');
    } catch (error) {
      handleApiError(error, '创建客户失败');
    }
  },

  updateCompany: async (id, updates) => {
    try {
      await companyService.update(id, updates);
      set((state) => ({
        companies: state.companies.map((c) =>
          c.id === id ? { ...c, ...updates } : c
        ),
      }));
      message.success('客户更新成功');
    } catch (error) {
      handleApiError(error, '更新客户失败');
    }
  },

  deleteCompany: async (id) => {
    try {
      await companyService.delete(id);
      set((state) => ({ companies: state.companies.filter((c) => c.id !== id) }));
      message.success('客户删除成功');
    } catch (error) {
      handleApiError(error, '删除客户失败');
    }
  },

  // ==================== Contact actions ====================
  fetchContacts: async () => {
    set((state) => ({ loading: { ...state.loading, contacts: true } }));
    try {
      // 后端 PageQuery 单页最多 100 条，需分页拉齐（客户列表上的联系人数依赖全量）
      const pageSize = 100;
      const maxPages = 200;
      const all: Contact[] = [];
      for (let pageNum = 1; pageNum <= maxPages; pageNum++) {
        const response = await services.getContacts({ pageNum, pageSize }) as Result<
          { records?: Contact[]; total?: number } | Contact[]
        >;
        if (response.code !== 200 || !response.data) break;
        const raw = response.data;
        const list = Array.isArray(raw) ? raw : raw.records ?? [];
        all.push(...list);
        const total = Array.isArray(raw) ? undefined : raw.total;
        if (list.length < pageSize) break;
        if (typeof total === 'number' && all.length >= total) break;
      }
      set({ contacts: dedupeById(all) });
    } catch (error) {
      handleApiError(error, '获取联系人列表失败');
    } finally {
      set((state) => ({ loading: { ...state.loading, contacts: false } }));
    }
  },

  addContact: async (contact) => {
    try {
      const response = await services.createContact(contact) as Result<Contact>;
      if (response.code === 200 && response.data) {
        set((state) => ({ contacts: [...state.contacts, response.data] }));
        message.success('联系人创建成功');
      }
    } catch (error) {
      handleApiError(error, '创建联系人失败');
    }
  },

  updateContact: async (id, updates) => {
    try {
      const response = await services.updateContact(id, updates) as Result<Contact>;
      if (response.code === 200 && response.data) {
        set((state) => ({
          contacts: state.contacts.map((c) =>
            c.id === id ? response.data : c
          )
        }));
        message.success('联系人更新成功');
      }
    } catch (error) {
      handleApiError(error, '更新联系人失败');
    }
  },

  deleteContact: async (id) => {
    try {
      const response = await services.deleteContact(id) as Result<void>;
      if (response.code === 200) {
        set((state) => ({ contacts: state.contacts.filter((c) => c.id !== id) }));
        message.success('联系人删除成功');
      }
    } catch (error) {
      handleApiError(error, '删除联系人失败');
    }
  },

  getContactsByCompany: (companyId) => {
    return get().contacts.filter((c) => c.companyId === companyId);
  },

  // ==================== Customer actions (兼容旧版) ====================
  addCustomer: async (customer) => {
    const company: Company = {
      id: customer.id,
      name: customer.companyName,
      industry: customer.industry,
      region: customer.region,
      status: customer.status,
      address: '',
      website: '',
      description: '',
      ownerName: customer.owner,
      createdAt: customer.createdAt,
      updatedAt: customer.updatedAt,
      lastContact: customer.lastContact,
      tags: customer.tags,
    };
    await get().addCompany(company);
  },

  updateCustomer: async (id, updates) => {
    const companyUpdates: Partial<Company> = {};
    if (updates.name) companyUpdates.name = updates.name;
    if (updates.status) companyUpdates.status = updates.status;
    if (updates.owner) companyUpdates.ownerName = updates.owner;
    if (updates.tags) companyUpdates.tags = updates.tags;
    await get().updateCompany(id, companyUpdates);
  },

  deleteCustomer: async (id) => {
    await get().deleteCompany(id);
  },

  // ==================== Opportunity actions ====================
  fetchOpportunities: async () => {
    set((state) => ({ loading: { ...state.loading, opportunities: true } }));
    try {
      const raw = await services.getOpportunities({
        pageNum: 1,
        pageSize: 100,
        outcomeCategory: 'ALL',
      });
      const response = getAxiosBody<
        Result<{ records?: Record<string, unknown>[]; total?: number } | Record<string, unknown>[]>
      >(raw);
      if (response?.code === 200 && response.data != null) {
        const d = response.data as { records?: Record<string, unknown>[] } | Record<string, unknown>[];
        const list = Array.isArray(d) ? d : d.records ?? [];
        const opportunities = list.map((row) => normalizeOpportunityFromApi(row));
        set({ opportunities });
      }
    } catch (error) {
      handleApiError(error, '获取商机列表失败');
    } finally {
      set((state) => ({ loading: { ...state.loading, opportunities: false } }));
    }
  },

  addOpportunity: async (opportunity) => {
    try {
      const response = await services.createOpportunity(opportunity as unknown as Record<string, unknown>) as Result<string>;
      if (response.code === 200) {
        message.success('商机创建成功');
        await get().fetchOpportunities();
      }
    } catch (error) {
      handleApiError(error, '创建商机失败');
    }
  },

  updateOpportunity: async (id, updates) => {
    try {
      const response = await services.updateOpportunity(id, updates as unknown as Record<string, unknown>) as Result<void>;
      assertResultOk(response, '更新商机失败');
      await get().fetchOpportunities();
    } catch (error) {
      handleApiError(error, '更新商机失败');
      throw error;
    }
  },

  deleteOpportunity: async (id) => {
    try {
      const response = await services.deleteOpportunity(id) as Result<void>;
      if (response.code === 200) {
        set((state) => ({ opportunities: state.opportunities.filter((o) => o.id !== id) }));
        message.success('商机删除成功');
      }
    } catch (error) {
      handleApiError(error, '删除商机失败');
    }
  },

  // ==================== Task actions ====================
  fetchTasks: async (opts?: { maxPages?: number }) => {
    if (skipFetchIfNoToken('fetchTasks')) return;
    set((state) => ({ loading: { ...state.loading, tasks: true } }));
    try {
      const pageSize = 100;
      const maxPages = opts?.maxPages ?? 100;
      const all: Task[] = [];
      for (let pageNum = 1; pageNum <= maxPages; pageNum++) {
        const raw = await services.getTasks({ pageNum, pageSize });
        const response = getAxiosBody<
          Result<{ records?: Record<string, unknown>[]; total?: number }>
        >(raw);
        if (response?.code !== 200 || !response.data) break;
        const rec = response.data.records ?? [];
        all.push(...rec.map((r) => mapApiTaskRecordToTask(r)));
        const total = response.data.total;
        if (rec.length < pageSize) break;
        if (typeof total === 'number' && all.length >= total) break;
      }
      set({ tasks: all });
    } catch (error) {
      handleApiError(error, '获取任务列表失败');
    } finally {
      set((state) => ({ loading: { ...state.loading, tasks: false } }));
    }
  },

  addTask: async (task, options) => {
    try {
      const raw = await services.createTask(taskToSaveParams(task));
      const response = getAxiosBody<Result<string>>(raw);
      if (response?.code === 200 && response.data) {
        const newTask: Task = { ...task, id: response.data };
        set((state) => ({ tasks: [...state.tasks, newTask] }));
        let synced = 0;
        if (!options?.skipAssigneeScheduleSync) {
          synced = await syncProjectTaskAssigneeSchedules(get as ScheduleSyncGetter, newTask);
        }
        if (!options?.quiet) {
          message.success('任务创建成功');
        }
        return synced;
      }
    } catch (error) {
      handleApiError(error, '创建任务失败');
    }
    return 0;
  },

  updateTask: async (id, updates) => {
    try {
      const existing = get().tasks.find((t) => t.id === id);
      const raw = await services.updateTask(id, taskToSaveParams(updates, existing));
      const response = getAxiosBody<Result<void>>(raw);
      if (response?.code === 200) {
        const merged: Task = { ...(existing as Task), ...updates } as Task;
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === id ? { ...t, ...updates } : t
          )
        }));
        const synced = await replaceDerivedSchedulesForProjectTask(get as ScheduleSyncGetter, merged);
        message.success('任务更新成功');
        return synced;
      }
    } catch (error) {
      handleApiError(error, '更新任务失败');
    }
    return 0;
  },

  deleteTask: async (id) => {
    try {
      const raw = await services.deleteTask(id);
      const response = getAxiosBody<Result<void>>(raw);
      if (response?.code === 200) {
        const prefix = `${id}_schedule_`;
        const derived = get().schedules.filter((s) => s.id.startsWith(prefix));
        for (const s of derived) {
          await get().deleteSchedule(s.id, { quiet: true });
        }
        set((state) => ({ tasks: state.tasks.filter((t) => t.id !== id) }));
        message.success('任务删除成功');
      }
    } catch (error) {
      handleApiError(error, '删除任务失败');
    }
  },

  completeTask: async (id) => {
    try {
      const raw = await services.completeTask(id);
      const response = getAxiosBody<Result<void>>(raw);
      if (response?.code === 200) {
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === id ? { ...t, status: 'completed', completedAt: getCurrentDate() } : t
          )
        }));
        await get().fetchSchedules();
        message.success('任务已完成');
      }
    } catch (error) {
      handleApiError(error, '完成任务失败');
    }
  },

  reopenTask: async (id) => {
    try {
      const raw = await services.reopenTask(id);
      const response = getAxiosBody<Result<void>>(raw);
      if (response?.code === 200) {
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === id ? { ...t, status: 'in_progress' } : t
          )
        }));
        message.success('任务已重新开启');
      }
    } catch (error) {
      handleApiError(error, '重新开启任务失败');
    }
  },

  // ==================== Project actions ====================
  fetchProjects: async () => {
    set((state) => ({ loading: { ...state.loading, projects: true } }));
    try {
      const pageSize = 100;
      const maxPages = 200;
      const all: Project[] = [];
      for (let pageNum = 1; pageNum <= maxPages; pageNum++) {
        const raw = await services.getProjects({ pageNum, pageSize });
        const response = getAxiosBody<Result<{
          records?: Project[];
          total?: number;
        }>>(raw);
        if (response?.code !== 200 || !response.data) break;
        const rec = response.data.records ?? [];
        all.push(
          ...rec.map((r) => normalizeProjectFromApi(r as unknown as Record<string, unknown>))
        );
        const total = response.data.total;
        if (rec.length < pageSize) break;
        if (typeof total === 'number' && all.length >= total) break;
      }
      set({ projects: all });
    } catch (error) {
      handleApiError(error, '获取项目列表失败');
    } finally {
      set((state) => ({ loading: { ...state.loading, projects: false } }));
    }
  },

  fetchProjectById: async (id: string) => {
    const projectId = id?.trim();
    if (!isValidEntityId(projectId)) {
      if (projectId) logger.warn('跳过非法项目ID详情请求', { id: projectId });
      return;
    }
    try {
      const raw = await services.getProjectById(projectId);
      const response = getAxiosBody<Result<Project>>(raw);
      if (response?.code !== 200 || !response.data) return;
      const incoming = response.data;
      const data = normalizeProjectFromApi(incoming as unknown as Record<string, unknown>);
      if (incoming.attachments != null && !Array.isArray(incoming.attachments)) {
        data.attachments = [];
      }
      set((state) => ({
        projects: state.projects.some((p) => p.id === projectId)
          ? state.projects.map((p) => (p.id === projectId ? { ...p, ...data } : p))
          : [...state.projects, data],
      }));
    } catch (error) {
      handleApiError(error, '刷新项目详情失败');
    }
  },

  addProject: async (project) => {
    try {
      const response = await services.createProject(project as unknown as Record<string, unknown>) as Result<string>;
      if (response.code === 200 && response.data) {
        const p = normalizeProjectFromApi({ ...(project as unknown as Record<string, unknown>), id: response.data });
        set((state) => ({ projects: [...state.projects, p] }));
        message.success('项目创建成功');
      }
    } catch (error) {
      handleApiError(error, '创建项目失败');
    }
  },

  updateProject: async (id, updates) => {
    try {
      const response = await services.updateProject(id, updates) as Result<void>;
      if (response.code === 200) {
        set((state) => ({
          projects: state.projects.map((p) =>
            p.id === id ? { ...p, ...updates } : p
          )
        }));
        return;
      }
      throw new Error(response.message || '更新项目失败');
    } catch (error) {
      handleApiError(error, '更新项目失败');
      throw error;
    }
  },

  updateProjectProgressOnly: async (id, progress) => {
    try {
      const response = await services.updateProjectProgress(id, progress) as Result<unknown>;
      if (response.code === 200) {
        set((state) => ({
          projects: state.projects.map((p) => (p.id === id ? { ...p, progress } : p)),
        }));
      }
    } catch (error) {
      handleApiError(error, '更新项目进度失败');
    }
  },

  deleteProject: async (id) => {
    try {
      const response = await services.deleteProject(id) as Result<void>;
      if (response.code === 200) {
        set((state) => ({ projects: state.projects.filter((p) => p.id !== id) }));
        message.success('项目删除成功');
      }
    } catch (error) {
      handleApiError(error, '删除项目失败');
    }
  },

  // ==================== Schedule actions ====================
  /** 后端 Jackson 约定 yyyy-MM-dd HH:mm:ss；前端 RangePicker 常为 HH:mm，补 :00 */
  normalizeSchedulePayload: (raw: Record<string, unknown>): Record<string, unknown> => {
    const out = { ...raw };
    const padApiDateTime = (v: unknown): unknown => {
      if (typeof v !== 'string') return v;
      const t = v.trim();
      if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(t)) return `${t}:00`;
      return v;
    };
    if (out.startTime != null) out.startTime = padApiDateTime(out.startTime);
    if (out.endTime != null) out.endTime = padApiDateTime(out.endTime);
    const hasDesc = Object.prototype.hasOwnProperty.call(out, 'description');
    const hasSt = Object.prototype.hasOwnProperty.call(out, 'scheduleSubtype');
    if (hasDesc || hasSt) {
      const desc = hasDesc ? String(out.description ?? '') : '';
      const st = hasSt && typeof out.scheduleSubtype === 'string' ? out.scheduleSubtype : undefined;
      out.content = buildScheduleContentPayload(desc, st);
      delete out.description;
      delete out.scheduleSubtype;
    }
    const parts = out.participants;
    if (Array.isArray(parts)) {
      out.participants = JSON.stringify(parts);
    }
    const contactIds = out.contactIds;
    if (Array.isArray(contactIds)) {
      out.contactIds = JSON.stringify(contactIds.map((x) => String(x).trim()).filter(Boolean));
    }
    const contactNames = out.contactNames;
    if (Array.isArray(contactNames)) {
      out.contactNames = JSON.stringify(contactNames.map((x) => String(x).trim()).filter(Boolean));
    }
    const rem = out.reminder as { minutes?: number } | undefined;
    if (rem && typeof rem === 'object' && rem.minutes != null) {
      out.remind = String(rem.minutes);
    }
    delete out.reminder;
    const st = out.status as string | undefined;
    if (st === 'scheduled') {
      out.status = 'pending';
    }
    // 任务同步的项目日程：companyId 即客户 id，后端 ScheduleSaveDTO 要求 customerId
    if (!out.customerId && out.companyId) {
      out.customerId = out.companyId;
    }
    if (!out.customerName && out.companyName) {
      out.customerName = out.companyName;
    }
    // 仅前端使用的字段，勿传给 ScheduleSaveDTO
    for (const key of [
      'id',
      'createdAt',
      'updatedAt',
      'completedAt',
      'projectTaskAssignees',
      'companyId',
      'companyName',
    ] as const) {
      delete out[key];
    }
    return out;
  },

  // 将后端 type 映射为前端 category，并填充公司名称
  mapScheduleTypeToCategory: (schedule: Schedule): Schedule => {
    const typeToCategory: Record<string, string> = {
      'meeting': 'daily',     // 会议 -> 日常
      'call': 'daily',        // 通话 -> 日常
      'event': 'market',      // 活动 -> 市场
      'training': 'market',   // 培训 -> 市场
      'demo': 'opportunity',  // 演示 -> 商机
      'proposal': 'opportunity', // 提案 -> 商机
      'project': 'project',   // 项目 -> 项目
      'visit': 'opportunity', // 拜访类 -> 商机（与后端 ScheduleTypeMapping 一致）
    };
    const raw = schedule as unknown as Record<string, unknown>;
    const startFallback =
      (typeof raw.startTime === 'string' && raw.startTime.trim()) ||
      (typeof raw.startDate === 'string' && raw.startDate.trim()) ||
      (typeof raw.start_time === 'string' && raw.start_time.trim()) ||
      (typeof raw.beginTime === 'string' && raw.beginTime.trim()) ||
      '';
    if (startFallback && !String(schedule.startTime || '').trim()) {
      schedule.startTime = /\s|T/.test(startFallback) ? startFallback : `${startFallback} 09:00:00`;
    }
    const rawLinked = raw.linkedTaskId;
    if (rawLinked != null && String(rawLinked).trim()) {
      schedule.linkedTaskId = String(rawLinked).trim();
    }
    const endFallback =
      (typeof raw.endTime === 'string' && raw.endTime.trim()) ||
      (typeof raw.endDate === 'string' && raw.endDate.trim()) ||
      (typeof raw.end_time === 'string' && raw.end_time.trim()) ||
      '';
    if (endFallback && !String(schedule.endTime || '').trim()) {
      schedule.endTime = /\s|T/.test(endFallback) ? endFallback : `${endFallback} 18:00:00`;
    }
    const rawContent = typeof raw.content === 'string' ? raw.content : undefined;
    const parsedContent = parseScheduleContentPayload(rawContent);
    schedule.description = parsedContent.body || (typeof raw.description === 'string' ? raw.description : '');
    if (parsedContent.scheduleSubtype) {
      schedule.scheduleSubtype = parsedContent.scheduleSubtype;
    }
    const apiStatus = typeof raw.status === 'string' ? raw.status : undefined;
    (schedule as Schedule & { apiStatus?: string }).apiStatus = apiStatus;
    if (apiStatus === 'pending' || apiStatus === 'ongoing') {
      schedule.status = 'scheduled';
    } else if (apiStatus === 'completed') {
      schedule.status = 'completed';
    } else if (apiStatus === 'cancelled' || apiStatus === 'not_executed') {
      schedule.status = 'cancelled';
    }
    const apiType = typeof raw.type === 'string' ? raw.type : undefined;
    if (apiType && !schedule.category) {
      schedule.category = (typeToCategory[apiType] || 'daily') as ScheduleCategory;
    }

    // 已关联项目或商机时，日历/筛选的大类与配色必须以关联为准。
    // 后端常见存 type=meeting（映射为日常），但若带了 projectId/opportunityId 仍应显示为项目/商机。
    const pid = schedule.projectId ?? (typeof raw.projectId === 'string' ? raw.projectId : undefined);
    const oid = schedule.opportunityId ?? (typeof raw.opportunityId === 'string' ? raw.opportunityId : undefined);
    if (pid && String(pid).trim() !== '') {
      schedule.category = 'project';
      schedule.projectId = String(pid).trim();
    } else if (oid && String(oid).trim() !== '') {
      schedule.category = 'opportunity';
      schedule.opportunityId = String(oid).trim();
    }

    // 客户经理（owner）：后端存用户 ID；前端展示姓名，但保留 ownerId/ownerName 供编辑回填与保存
    if (schedule.owner) {
      const rawOwnerId = String(schedule.owner).trim();
      if (rawOwnerId) {
        schedule.ownerId = rawOwnerId;
      }
      const employees = get().users;
      const employee = employees.find((emp: User) => emp.id === rawOwnerId);
      if (employee) {
        schedule.ownerName = employee.name;
        schedule.owner = employee.name;
      }
    }
    
    // 客户/公司主键：后端 VO 多为 customerId（表 company_id），与前端 companyId 对齐
    const rawCust = typeof raw.customerId === 'string' ? raw.customerId.trim() : '';
    const rawComp = typeof raw.companyId === 'string' ? raw.companyId.trim() : '';
    const companyKey = String(schedule.companyId || schedule.customerId || rawComp || rawCust || '').trim();
    if (companyKey) {
      if (!schedule.companyId) schedule.companyId = companyKey;
      if (!schedule.customerId) schedule.customerId = companyKey;
    }
    if (companyKey && !schedule.companyName?.trim()) {
      const company = get().companies.find((c: Company) => c.id === companyKey);
      if (company) {
        schedule.companyName = company.name;
      }
    }
    if (companyKey && !schedule.customerName?.trim() && schedule.companyName?.trim()) {
      schedule.customerName = schedule.companyName;
    }

    const normalizeTextArray = (value: unknown): string[] => {
      if (Array.isArray(value)) {
        return value.map((item) => String(item ?? '').trim()).filter(Boolean);
      }
      if (typeof value !== 'string') return [];
      const trimmed = value.trim();
      if (!trimmed) return [];
      if (trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed) as unknown[];
          return Array.isArray(parsed)
            ? parsed.map((item) => String(item ?? '').trim()).filter(Boolean)
            : [];
        } catch {
          // fall through to comma split
        }
      }
      return trimmed.split(',').map((item) => item.trim()).filter(Boolean);
    };
    const rawContactIds = (raw as { contactIds?: unknown; contact_ids?: unknown }).contactIds
      ?? (raw as { contact_ids?: unknown }).contact_ids;
    const rawContactNames = (raw as { contactNames?: unknown; contact_names?: unknown }).contactNames
      ?? (raw as { contact_names?: unknown }).contact_names;
    const contactIds = normalizeTextArray(rawContactIds);
    const contactNames = normalizeTextArray(rawContactNames);
    if (rawContactIds !== undefined || !Array.isArray(schedule.contactIds)) {
      schedule.contactIds = contactIds;
    }
    if (rawContactNames !== undefined || !Array.isArray(schedule.contactNames)) {
      schedule.contactNames = contactNames;
    }

    const projId = String(schedule.projectId || '').trim();
    if (projId && !schedule.projectName?.trim()) {
      const proj = get().projects.find((p: Project) => p.id === projId);
      if (proj?.name) {
        schedule.projectName = proj.name;
        if (!schedule.companyName?.trim() && proj.companyName) schedule.companyName = proj.companyName;
        if (!schedule.customerName?.trim() && proj.companyName) schedule.customerName = proj.companyName;
      }
    }

    const oppId = String(schedule.opportunityId || '').trim();
    if (oppId && !schedule.opportunityName?.trim()) {
      const opp = get().opportunities.find((o: Opportunity) => o.id === oppId);
      if (opp?.name) {
        schedule.opportunityName = opp.name;
        if (!schedule.customerName?.trim() && opp.companyName) schedule.customerName = opp.companyName;
        if (!schedule.companyName?.trim() && opp.companyName) schedule.companyName = opp.companyName;
      }
    }

    // 处理 participants - 后端可能为 JSON 数组字符串或逗号分隔
    const partUnknown = schedule.participants as unknown;
    if (typeof partUnknown === 'string' && partUnknown) {
      const trimmed = partUnknown.trim();
      if (trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed) as Array<{ id?: string; name?: string }>;
          schedule.participants = parsed.map((p) => ({
            id: p.id || '',
            name: p.name || '',
          })) as ScheduleParticipant[];
        } catch {
          const names = partUnknown.split(',').map((n: string) => n.trim()).filter(Boolean);
          schedule.participants = names.map((name: string) => ({ id: name, name })) as ScheduleParticipant[];
        }
      } else {
        const names = partUnknown.split(',').map((n: string) => n.trim()).filter(Boolean);
        schedule.participants = names.map((name: string) => ({ id: name, name })) as ScheduleParticipant[];
      }
    } else if (!schedule.participants) {
      schedule.participants = [];
    }
    
    return schedule;
  },

  fetchSchedules: async () => {
    if (skipFetchIfNoToken('fetchSchedules')) return;
    set((state) => ({ loading: { ...state.loading, schedules: true } }));
    try {
      const pageSize = 100;
      const maxPages = 50;
      const all: Schedule[] = [];
      for (let pageNum = 1; pageNum <= maxPages; pageNum++) {
        const raw = await services.getSchedules({ pageNum, pageSize });
        const response = getAxiosBody<{ code?: number; data?: { records?: Schedule[]; total?: number } | Schedule[] }>(raw);
        if (response?.code !== 200 || !response.data) break;
        const data = response.data;
        const records = Array.isArray(data) ? data : data.records || [];
        all.push(...records.map((s) => get().mapScheduleTypeToCategory(s)));
        const total = Array.isArray(data) ? records.length : data.total;
        if (records.length < pageSize) break;
        if (typeof total === 'number' && all.length >= total) break;
      }
      set({ schedules: dedupeById(all) });
    } catch (error) {
      handleApiError(error, '获取日程列表失败');
    } finally {
      set((state) => ({ loading: { ...state.loading, schedules: false } }));
    }
  },

  addSchedule: async (schedule, options) => {
    try {
      const categoryToType: Record<string, string> = {
        daily: 'meeting',
        market: 'event',
        opportunity: 'demo',
        project: 'project',
        client: 'visit',
      };
      const raw = {
        ...(schedule as unknown as Record<string, unknown>),
        type:
          (schedule as unknown as Record<string, unknown>).type ||
          categoryToType[schedule.category || 'daily'] ||
          'meeting',
      } as Record<string, unknown>;
      const scheduleData = get().normalizeSchedulePayload(raw);
      const rawResp = await services.createSchedule(scheduleData);
      const response = getAxiosBody<Result<string | Schedule>>(rawResp);
      if (response?.code === 200) {
        const createdId =
          typeof response.data === 'string'
            ? response.data
            : (response.data as Schedule | undefined)?.id ?? null;
        if (!options?.skipFetch) {
          await get().fetchSchedules();
        }
        if (!options?.quiet) {
          message.success('日程创建成功');
        }
        return createdId;
      }
    } catch (error) {
      handleApiError(error, '创建日程失败');
    }
    return null;
  },

  updateSchedule: async (id, updates) => {
    try {
      const categoryToType: Record<string, string> = {
        daily: 'meeting',
        market: 'event',
        opportunity: 'demo',
        project: 'project',
        client: 'visit',
      };
      const raw = {
        ...updates,
        type: (updates as Record<string, unknown>).type || categoryToType[updates.category || 'daily'] || 'meeting',
      } as Record<string, unknown>;
      const scheduleData = get().normalizeSchedulePayload(raw);
      const response = await services.updateSchedule(id, scheduleData) as Result<void>;
      if (response.code === 200) {
        await get().fetchSchedules();
        message.success('日程更新成功');
        return true;
      }
    } catch (error) {
      handleApiError(error, '更新日程失败');
    }
    return false;
  },

  deleteSchedule: async (id, options) => {
    try {
      const response = await services.deleteSchedule(id) as Result<void>;
      if (response.code === 200) {
        set((state) => ({ schedules: state.schedules.filter((s) => s.id !== id) }));
        if (!options?.quiet) {
          message.success('日程删除成功');
        }
      }
    } catch (error) {
      handleApiError(error, '删除日程失败');
    }
  },

  // ==================== Contract actions ====================
  fetchContracts: async () => {
    set((state) => ({ contractsLoaded: false, loading: { ...state.loading, finance: true } }));
    try {
      const pageSize = 100;
      const maxPages = 200;
      const all: Contract[] = [];
      let completed = false;
      for (let pageNum = 1; pageNum <= maxPages; pageNum++) {
        const raw = await services.getContracts({ pageNum, pageSize });
        if (raw.code !== 200 || !raw.data?.records) {
          throw new Error(raw.message || '获取合同列表失败');
        }
        const records = raw.data.records.map((r) =>
          normalizeContractFinToUi(r as unknown as Record<string, unknown>)
        );
        all.push(...records);
        const total = raw.data.total;
        if (records.length < pageSize || (typeof total === 'number' && all.length >= total)) {
          completed = true;
          break;
        }
      }
      if (!completed) {
        throw new Error('合同列表超过分页加载上限，无法生成完整回款汇总');
      }
      set({ contracts: all, contractsLoaded: true });
      const projectIds = uniqueValidEntityIds(all.map((c) => c.projectId));
      await Promise.all(projectIds.map((pid) => get().fetchProjectById(pid)));
    } catch (error) {
      handleApiError(error, '获取合同列表失败');
    } finally {
      set((state) => ({ loading: { ...state.loading, finance: false } }));
    }
  },

  addContract: async (contract) => {
    try {
      const response = await services.createContract(contract as unknown as Record<string, unknown>) as Result<string>;
      if (response.code === 200 && response.data) {
        const c = normalizeContractFinToUi({ ...(contract as unknown as Record<string, unknown>), id: response.data });
        set((state) => ({ contracts: [...state.contracts, c] }));
        message.success('合同创建成功');
      }
    } catch (error) {
      handleApiError(error, '创建合同失败');
    }
  },

  updateContract: async (id, updates) => {
    try {
      const response = await services.updateContract(id, updates) as Result<void>;
      if (response.code === 200) {
        await get().fetchContracts();
        message.success('合同更新成功');
      } else {
        throw new Error(response.message || '更新合同失败');
      }
    } catch (error) {
      handleApiError(error, '更新合同失败');
      throw error;
    }
  },

  deleteContract: async (id) => {
    try {
      const response = await services.deleteContract(id) as Result<void>;
      if (response.code === 200) {
        set((state) => ({ contracts: state.contracts.filter((c) => c.id !== id) }));
        message.success('合同删除成功');
      }
    } catch (error) {
      handleApiError(error, '删除合同失败');
    }
  },

  // ==================== ReceivablePlan actions ====================
  fetchReceivablePlans: async () => {
    try {
      const raw = await services.getReceivablePlans({});
      const body = getAxiosBody<unknown>(raw);
      const rows = unwrapListBody<Record<string, unknown>>(body);
      set({ receivablePlans: rows.map((row) => mapReceivableVoToPlan(row)) });
    } catch (error) {
      handleApiError(error, '获取应收款计划失败');
    }
  },

  addReceivablePlan: async (plan) => {
    try {
      const payload = mapReceivablePlanToApiPayload(plan);
      const response = await services.createReceivablePlan(payload) as Result<string>;
      if (response.code === 200) {
        await get().fetchReceivablePlans();
        message.success('应收款计划创建成功');
      }
    } catch (error) {
      handleApiError(error, '创建应收款计划失败');
    }
  },

  updateReceivablePlan: async (id, updates) => {
    try {
      const current = get().receivablePlans.find((p) => p.id === id);
      const merged = { ...(current ?? {}), ...(updates as Partial<ReceivablePlan>) };
      const payload = mapReceivablePlanToApiPayload(merged);
      const response = await services.updateReceivablePlan(id, payload) as Result<void>;
      if (response.code === 200) {
        await get().fetchReceivablePlans();
        message.success('应收款计划更新成功');
      } else {
        throw new Error(response.message || '更新应收款计划失败');
      }
    } catch (error) {
      handleApiError(error, '更新应收款计划失败');
      throw error;
    }
  },

  deleteReceivablePlan: async (id) => {
    try {
      const response = await services.deleteReceivablePlan(id) as Result<void>;
      if (response.code === 200) {
        set((state) => ({ receivablePlans: state.receivablePlans.filter((p) => p.id !== id) }));
        message.success('应收款计划删除成功');
      }
    } catch (error) {
      handleApiError(error, '删除应收款计划失败');
    }
  },

  // ==================== PaymentRecord actions ====================
  fetchPaymentRecords: async () => {
    set({ paymentRecordsLoaded: false });
    try {
      const pageSize = 100;
      const maxPages = 200;
      const all: PaymentRecord[] = [];
      let completed = false;

      for (let pageNum = 1; pageNum <= maxPages; pageNum++) {
        const raw = await services.getPaymentRecords({ pageNum, pageSize });
        const body = getAxiosBody<unknown>(raw);
        assertResultOk(body, '获取回款记录失败');
        const { records, total } = unwrapResultPageWithTotal<PaymentRecord>(body);
        all.push(...records);
        if (records.length < pageSize || (total > 0 && all.length >= total)) {
          completed = true;
          break;
        }
      }

      if (!completed) {
        throw new Error('回款记录超过分页加载上限');
      }
      set({ paymentRecords: all, paymentRecordsLoaded: true });
    } catch (error) {
      handleApiError(error, '获取回款记录失败');
    }
  },

  addPaymentRecord: async (record) => {
    try {
      const response = await services.createPaymentRecord(record as unknown as Record<string, unknown>) as Result<string>;
      if (response.code === 200 && response.data) {
        set((state) => ({ paymentRecords: [...state.paymentRecords, { ...record, id: response.data } as PaymentRecord] }));
        message.success('回款记录创建成功');
        return true;
      }
      return false;
    } catch (error) {
      handleApiError(error, '创建回款记录失败');
      return false;
    }
  },

  updatePaymentRecord: async (id, updates) => {
    try {
      const response = await services.updatePaymentRecord(id, updates as unknown as Record<string, unknown>) as Result<void>;
      if (response.code === 200) {
        set((state) => ({
          paymentRecords: state.paymentRecords.map((r) =>
            r.id === id ? { ...r, ...updates } : r
          )
        }));
        message.success('回款记录更新成功');
      }
    } catch (error) {
      handleApiError(error, '更新回款记录失败');
    }
  },

  deletePaymentRecord: async (id) => {
    try {
      const response = await services.deletePaymentRecord(id) as Result<void>;
      if (response.code === 200) {
        set((state) => ({ paymentRecords: state.paymentRecords.filter((r) => r.id !== id) }));
        message.success('回款记录删除成功');
      }
    } catch (error) {
      handleApiError(error, '删除回款记录失败');
    }
  },

  // ==================== Invoice actions ====================
  fetchInvoices: async () => {
    try {
      const pageSize = 500;
      let pageNum = 1;
      let total = Number.MAX_SAFE_INTEGER;
      const rows: Record<string, unknown>[] = [];

      while (rows.length < total) {
        const raw = await services.getInvoices({ pageNum, pageSize });
        const body = getAxiosBody<unknown>(raw);
        const page = unwrapResultPageWithTotal<Record<string, unknown>>(body);
        const batch = page.records.length > 0 ? page.records : unwrapListBody<Record<string, unknown>>(body);
        if (batch.length === 0) break;
        rows.push(...batch);
        if (page.records.length === 0) break;
        total = Math.max(page.total || 0, rows.length);
        if (batch.length < pageSize) break;
        pageNum += 1;
      }
      set({ invoices: rows.map(mapInvoiceVoToInvoice) });
    } catch (error) {
      handleApiError(error, '获取发票列表失败');
    }
  },

  addInvoice: async (invoice) => {
    try {
      const response = await services.createInvoice(invoice as unknown as Record<string, unknown>) as Result<string>;
      if (response.code === 200 && response.data) {
        await get().fetchInvoices();
      }
    } catch (error) {
      handleApiError(error, '创建发票失败');
    }
  },

  updateInvoice: async (id, updates) => {
    try {
      const response = await services.updateInvoice(id, updates) as Result<void>;
      if (response.code === 200) {
        set((state) => ({
          invoices: state.invoices.map((i) =>
            i.id === id ? { ...i, ...updates, updatedAt: dayjs().format('YYYY-MM-DD') } : i
          )
        }));
        message.success('发票更新成功');
      }
    } catch (error) {
      handleApiError(error, '更新发票失败');
    }
  },

  deleteInvoice: async (id) => {
    try {
      const response = await services.deleteInvoice(id) as Result<void>;
      if (response.code === 200) {
        set((state) => ({ invoices: state.invoices.filter((i) => i.id !== id) }));
        message.success('发票删除成功');
      }
    } catch (error) {
      handleApiError(error, '删除发票失败');
    }
  },

  // ==================== Expense actions ====================
  fetchExpenses: async (opts?: { maxPages?: number; pageSize?: number }) => {
    if (skipFetchIfNoToken('fetchExpenses')) return;
    try {
      const pageSize = opts?.pageSize ?? 500;
      const maxPages = opts?.maxPages ?? Number.MAX_SAFE_INTEGER;
      let pageNum = 1;
      let total = Number.MAX_SAFE_INTEGER;
      const rows: Record<string, unknown>[] = [];

      while (rows.length < total && pageNum <= maxPages) {
        const raw = await services.getExpenses({ pageNum, pageSize });
        const body = getAxiosBody<unknown>(raw);
        const page = unwrapResultPageWithTotal<Record<string, unknown>>(body);
        const batch = page.records.length > 0 ? page.records : unwrapListBody<Record<string, unknown>>(body);
        if (batch.length === 0) break;
        rows.push(...batch);
        if (page.records.length === 0) break;
        total = Math.max(page.total || 0, rows.length);
        if (batch.length < pageSize) break;
        pageNum += 1;
      }

      set({
        expenses: rows.map((row) => {
          const marketingActivityId = (row.marketingActivityId ?? row.marketing_activity_id) as string | undefined;
          let category = row.category as Expense['category'];
          if (!category && marketingActivityId) category = 'marketing';
          else if (!category && row.projectId) category = 'project';
          else if (!category && row.items) {
            try {
              const meta = JSON.parse(String(row.items)) as { category?: Expense['category'] };
              if (meta.category) category = meta.category;
            } catch {
              /* ignore */
            }
          }
          return {
            ...(row as unknown as Expense),
            companyId: (row.companyId ?? row.company_id) as string | undefined,
            companyName: (row.companyName ?? row.company_name) as string | undefined,
            applicant: String(row.applicantName ?? row.applicant ?? ''),
            expenseType: String(row.type ?? row.expenseType ?? ''),
            totalAmount: Number(row.totalAmount ?? row.amount ?? 0),
            amount: Number(row.amount ?? row.totalAmount ?? 0),
            marketingActivityId,
            category,
            submittedAt: String(row.submittedAt ?? row.submitted_at ?? '').trim() || undefined,
            approvedBy: (row.approvedBy ?? row.approved_by) as string | undefined,
            approvedByName: String(row.approvedByName ?? row.approved_by_name ?? '').trim() || undefined,
            approvedAt: String(row.approvedAt ?? row.approved_at ?? '').trim() || undefined,
            paidBy: (row.paidBy ?? row.paid_by) as string | undefined,
            paidByName: String(row.paidByName ?? row.paid_by_name ?? '').trim() || undefined,
            paidAt: String(row.paidAt ?? row.paid_at ?? '').trim() || undefined,
            rejectReason: String(row.rejectReason ?? row.reject_reason ?? '').trim() || undefined,
            rejectedAt: String(row.rejectedAt ?? row.rejected_at ?? '').trim() || undefined,
            createdAt: String(row.createdAt ?? row.createTime ?? '').slice(0, 10) || dayjs().format('YYYY-MM-DD'),
            updatedAt: String(row.updatedAt ?? row.updateTime ?? '').slice(0, 10) || dayjs().format('YYYY-MM-DD'),
          } as Expense;
        }),
      });
    } catch (error) {
      handleApiError(error, '获取报销列表失败');
    }
  },

  addExpense: async (expense) => {
    if (!hasAuthToken()) {
      handleApiError({ response: { status: 401 } }, '创建报销单失败，请重新登录');
      return false;
    }
    try {
      const payload = toExpenseSavePayload(expense as unknown as Record<string, unknown>);
      const response = await services.createExpense(payload) as Result<string>;
      if (response.code === 200 && response.data) {
        await get().fetchExpenses();
        message.success('报销单创建成功');
        return true;
      }
      message.error(response.message || '创建报销单失败');
      return false;
    } catch (error) {
      handleApiError(error, '创建报销单失败');
      return false;
    }
  },

  updateExpense: async (id, updates) => {
    if (!hasAuthToken()) {
      handleApiError({ response: { status: 401 } }, '更新报销单失败，请重新登录');
      return false;
    }
    try {
      const payload = toExpenseSavePayload({ ...(updates as Record<string, unknown>), id });
      const response = await services.updateExpense(id, payload) as Result<void>;
      if (response.code === 200) {
        await get().fetchExpenses();
        message.success('报销单更新成功');
        return true;
      }
      message.error(response.message || '更新报销单失败');
      return false;
    } catch (error) {
      handleApiError(error, '更新报销单失败');
      return false;
    }
  },

  deleteExpense: async (id) => {
    try {
      const response = await services.deleteExpense(id) as Result<void>;
      if (response.code === 200) {
        set((state) => ({ expenses: state.expenses.filter((e) => e.id !== id) }));
        message.success('报销单删除成功');
      } else {
        throw new Error(response.message || '删除报销单失败');
      }
    } catch (error) {
      handleApiError(error, '删除报销单失败');
      throw error;
    }
  },

  // ==================== Loan actions ====================
  fetchLoans: async () => {
    if (skipFetchIfNoToken('fetchLoans')) return;
    try {
      const raw = await services.getLoans({});
      const body = getAxiosBody<unknown>(raw);
      const rows = unwrapListBody<Record<string, unknown>>(body);
      set({ loans: rows.map((row) => mapLoanVoToLoan(row)) });
    } catch (error) {
      handleApiError(error, '获取借款单列表失败');
    }
  },

  addLoan: async (loan) => {
    try {
      const response = await services.createLoan(loan as unknown as Record<string, unknown>) as Result<string>;
      if (response.code === 200 && response.data) {
        const mapped = mapLoanVoToLoan({ ...(loan as unknown as Record<string, unknown>), id: response.data });
        set((state) => ({ loans: [...state.loans, mapped] }));
        message.success('借款单创建成功');
      }
    } catch (error) {
      handleApiError(error, '创建借款单失败');
    }
  },

  updateLoan: async (id, updates) => {
    try {
      const response = await services.updateLoan(id, updates as unknown as Record<string, unknown>) as Result<void>;
      if (response.code === 200) {
        await get().fetchLoans();
        message.success('借款单更新成功');
      } else {
        throw new Error(response.message || '更新借款单失败');
      }
    } catch (error) {
      handleApiError(error, '更新借款单失败');
      throw error;
    }
  },

  deleteLoan: async (id) => {
    try {
      const response = await services.deleteLoan(id) as Result<void>;
      if (response.code === 200) {
        set((state) => ({ loans: state.loans.filter((l) => l.id !== id) }));
        message.success('借款单删除成功');
      } else {
        throw new Error(response.message || '删除借款单失败');
      }
    } catch (error) {
      handleApiError(error, '删除借款单失败');
      throw error;
    }
  },

  // ==================== Payable actions ====================
  fetchPayables: async () => {
    try {
      const raw = await services.getPayables({});
      const body = getAxiosBody<unknown>(raw);
      const rows = unwrapListBody<Record<string, unknown>>(body);
      set({ payables: rows.map((row) => normalizePayableRow(row)) });
    } catch (error) {
      handleApiError(error, '获取应付款列表失败');
    }
  },

  addPayable: async (payable) => {
    try {
      const response = await services.createPayable(payable as unknown as Record<string, unknown>) as Result<string>;
      if (response.code === 200 && response.data) {
        const mapped = mapPayableVoToPayable({ ...(payable as unknown as Record<string, unknown>), id: response.data });
        set((state) => ({ payables: [...state.payables, mapped] }));
        message.success('应付款创建成功');
      }
    } catch (error) {
      handleApiError(error, '创建应付款失败');
    }
  },

  updatePayable: async (id, updates) => {
    try {
      const response = await services.updatePayable(id, updates as unknown as Record<string, unknown>) as Result<void>;
      if (response.code === 200) {
        await get().fetchPayables();
        message.success('应付款更新成功');
      } else {
        throw new Error(response.message || '更新应付款失败');
      }
    } catch (error) {
      handleApiError(error, '更新应付款失败');
      throw error;
    }
  },

  confirmPayable: async (id, payload) => {
    try {
      const response = await services.confirmPayment(id, payload as unknown as Record<string, unknown>) as Result<void>;
      if (response.code === 200) {
        await Promise.all([get().fetchPayables(), get().fetchPaymentRecords()]);
        message.success('付款成功');
        return true;
      }
      message.error(response.message || '付款失败');
      return false;
    } catch (error) {
      handleApiError(error, '付款失败');
      return false;
    }
  },

  deletePayable: async (id) => {
    try {
      const response = await services.deletePayable(id) as Result<void>;
      if (response.code === 200) {
        set((state) => ({ payables: state.payables.filter((p) => p.id !== id) }));
        message.success('应付款删除成功');
      } else {
        throw new Error(response.message || '删除应付款失败');
      }
    } catch (error) {
      handleApiError(error, '删除应付款失败');
      throw error;
    }
  },

  // ==================== FundRecord actions ====================
  fetchFundRecords: async () => {
    try {
      const raw = await services.getFundRecords({});
      const body = getAxiosBody<unknown>(raw);
      set({ fundRecords: unwrapListBody<FundRecord>(body) });
    } catch (error) {
      handleApiError(error, '获取资金流水失败');
    }
  },

  addFundRecord: async (record) => {
    try {
      const response = await services.createFundRecord(record as unknown as Record<string, unknown>) as Result<string>;
      if (response.code === 200 && response.data) {
        set((state) => ({ fundRecords: [...state.fundRecords, { ...record, id: response.data } as FundRecord] }));
        message.success('资金流水创建成功');
      }
    } catch (error) {
      handleApiError(error, '创建资金流水失败');
    }
  },

  updateFundRecord: async (id, updates) => {
    try {
      const response = await services.updateFundRecord(id, updates as unknown as Record<string, unknown>) as Result<void>;
      if (response.code === 200) {
        set((state) => ({
          fundRecords: state.fundRecords.map((r) =>
            r.id === id ? { ...r, ...updates } : r
          )
        }));
        message.success('资金流水更新成功');
      }
    } catch (error) {
      handleApiError(error, '更新资金流水失败');
    }
  },

  deleteFundRecord: async (id) => {
    try {
      const response = await services.deleteFundRecord(id) as Result<void>;
      if (response.code === 200) {
        set((state) => ({ fundRecords: state.fundRecords.filter((r) => r.id !== id) }));
        message.success('资金流水删除成功');
      }
    } catch (error) {
      handleApiError(error, '删除资金流水失败');
    }
  },

  // ==================== FinanceConfig actions ====================
  fetchFinanceConfig: async () => {
    try {
      const raw = await services.getFinanceConfig();
      const response = getAxiosBody<Result<Record<string, unknown>>>(raw);
      if (response && response.code === 200 && response.data) {
        set({ financeConfig: mapFinanceConfigFromApi(response.data) });
      } else {
        logger.debug('使用默认财务配置');
        set({ financeConfig: { id: '1', incomeCategories: [], expenseCategories: [], defaultTaxRate: 0.06, companyAccounts: [], companyFinanceInfo: [], updatedAt: getCurrentDateTime() } });
      }
    } catch {
      logger.debug('获取财务配置失败,使用默认配置');
      set({ financeConfig: { id: '1', incomeCategories: [], expenseCategories: [], defaultTaxRate: 0.06, companyAccounts: [], companyFinanceInfo: [], updatedAt: getCurrentDateTime() } });
    }
  },

  updateFinanceConfig: async (updates) => {
    try {
      const raw = await services.updateFinanceConfig(updates as unknown as Record<string, unknown>);
      const response = getAxiosBody<Result<Record<string, unknown>>>(raw);
      if (response?.code === 200) {
        if (response.data) {
          set({ financeConfig: mapFinanceConfigFromApi(response.data) });
        } else {
          set((state) => ({
            financeConfig: { ...state.financeConfig, ...updates, updatedAt: getCurrentDate() }
          }));
        }
        message.success('财务配置更新成功');
      } else if ((response as { message?: string } | undefined)?.message) {
        message.error((response as { message?: string }).message as string);
      }
    } catch (error) {
      handleApiError(error, '更新财务配置失败');
    }
  },

  // ==================== 按客户筛选数据的 selectors ====================
  getExpensesByCompany: (companyId) => {
    return get().expenses.filter(e => e.companyId === companyId);
  },
  getInvoicesByCompany: (companyId) => {
    return get().invoices.filter(i => i.companyId === companyId);
  },
  getContractsByCompany: (companyId) => {
    return get().contracts.filter(c => c.companyId === companyId);
  },
  getProjectsByCompany: (companyId) => {
    return get().projects.filter(p => p.companyId === companyId);
  },
  getOpportunitiesByCompany: (companyId) => {
    return get().opportunities.filter((o) => o.companyId === companyId);
  },

  // ==================== 月结费用 actions ====================
  fetchMonthlyExpenses: async (params) => {
    if (skipFetchIfNoToken('fetchMonthlyExpenses')) return;
    try {
      const raw = await services.getMonthlyExpenseLedger({
        ...buildSafePageParams(params as Record<string, unknown> | undefined),
      });
      const rows = unwrapListBody<Record<string, unknown>>(raw);
      set({
        monthlyExpenses: rows.map((row) => {
          const status = (row.status as MonthlyExpenseRecord['status']) ?? 'pending';
          const isPending = status === 'pending';
          return {
            id: String(row.id),
            version: row.version == null ? undefined : Number(row.version),
            recordNo: String(row.recordNo ?? ''),
            provider: row.provider as MonthlyExpenseRecord['provider'],
            providerName: String(row.providerName ?? ''),
            orderNo: String(row.orderNo ?? ''),
            handler: String(row.handler ?? row.handlerName ?? ''),
            handlerName: row.handlerName as string | undefined,
            expenseType: row.expenseType as MonthlyExpenseRecord['expenseType'],
            amount: Number(row.amount ?? 0),
            // 业务口径：导入待确认阶段仅展示费用类别与金额，归属信息确认后才显示
            category: isPending ? undefined : (row.category as MonthlyExpenseRecord['category']),
            projectId: isPending ? undefined : (row.projectId as string | undefined),
            projectName: isPending ? undefined : (row.projectName as string | undefined),
            opportunityId: isPending ? undefined : (row.opportunityId as string | undefined),
            opportunityName: isPending ? undefined : (row.opportunityName as string | undefined),
            marketingActivityId: isPending ? undefined : (row.marketingActivityId as string | undefined),
            marketingActivityName: isPending ? undefined : (row.marketingActivityName as string | undefined),
            companyId: isPending ? undefined : (row.companyId as string | undefined),
            status,
            confirmBy: row.confirmBy as string | undefined,
            confirmAt: row.confirmAt as string | undefined,
            remark: row.remark as string | undefined,
            createdAt: String(row.createTime ?? '').slice(0, 10) || dayjs().format('YYYY-MM-DD'),
            updatedAt: String(row.updateTime ?? '').slice(0, 10) || dayjs().format('YYYY-MM-DD'),
          };
        }),
      });
    } catch (error) {
      handleApiError(error, '获取费用分摊列表失败');
    }
  },

  addMonthlyExpense: async (expense, silent = false) => {
    try {
      const payload = toMonthlyExpenseCreatePayload(expense as unknown as Record<string, unknown>);
      const raw = await services.createMonthlyExpense(payload);
      const response = getAxiosBody<Result<unknown>>(raw);
      if (response?.code === 200) {
        if (!silent) {
          await get().fetchMonthlyExpenses();
          message.success('费用分摊记录添加成功');
        }
      } else {
        // 仅 throw，由 catch 内 handleApiError 统一弹一次，避免重复 toast
        throw new Error((response as { message?: string } | undefined)?.message || '添加费用分摊记录失败');
      }
    } catch (error) {
      if (!silent) {
        handleApiError(error, '添加费用分摊记录失败');
      }
      throw error;
    }
  },

  updateMonthlyExpense: async (id, updates) => {
    try {
      const raw = await services.updateMonthlyExpense(id, updates as unknown as Record<string, unknown>);
      const response = getAxiosBody<Result<unknown>>(raw);
      if (response?.code === 200) {
        await get().fetchMonthlyExpenses();
        message.success('费用分摊更新成功');
      } else {
        throw new Error((response as { message?: string } | undefined)?.message || '更新费用分摊记录失败');
      }
    } catch (error) {
      handleApiError(error, '更新费用分摊记录失败');
      throw error;
    }
  },

  deleteMonthlyExpense: async (id) => {
    try {
      const raw = await services.deleteMonthlyExpense(id);
      const response = getAxiosBody<Result<unknown>>(raw);
      if (response?.code === 200) {
        set((state) => ({ monthlyExpenses: state.monthlyExpenses.filter((e) => e.id !== id) }));
        message.success('费用分摊记录删除成功');
      } else if ((response as { message?: string } | undefined)?.message) {
        message.error((response as { message?: string }).message as string);
      } else {
        message.error('删除费用分摊记录失败');
      }
    } catch (error) {
      handleApiError(error, '删除费用分摊记录失败');
    }
  },

  // ==================== 供应商管理 actions（后端 /fin/suppliers，fin_supplier 表） ====================
  fetchSuppliers: async () => {
    try {
      const raw = await services.getSuppliers(buildSafePageParams());
      const body = getAxiosBody<Result<{ records?: Record<string, unknown>[] }>>(raw);
      if (body?.code === 200 && body.data?.records) {
        const list = body.data.records.map((r) => normalizeSupplierFromApi(r as Record<string, unknown>));
        set({ suppliers: list });
      }
    } catch (error) {
      handleApiError(error, '获取供应商列表失败');
    }
  },

  addSupplier: async (supplier) => {
    try {
      const payload = supplierToSavePayload(supplier);
      const raw = await services.createSupplier(payload);
      const body = getAxiosBody<Result<string>>(raw);
      if (body?.code === 200 && body.data) {
        await get().fetchSuppliers();
        message.success('供应商添加成功');
      } else {
        message.error((body as { msg?: string })?.msg ?? '添加供应商失败');
      }
    } catch (error) {
      handleApiError(error, '添加供应商失败');
    }
  },

  updateSupplier: async (id, updates) => {
    try {
      const prev = get().suppliers.find((s) => s.id === id);
      const merged = { ...(prev ?? ({} as Supplier)), ...updates, id } as Supplier;
      const raw = await services.updateSupplier(id, supplierToSavePayload(merged));
      const body = getAxiosBody<Result<unknown>>(raw);
      if (body?.code === 200) {
        await get().fetchSuppliers();
        message.success('供应商更新成功');
      } else {
        throw new Error((body as { msg?: string })?.msg ?? '更新供应商失败');
      }
    } catch (error) {
      handleApiError(error, '更新供应商失败');
      throw error;
    }
  },

  deleteSupplier: async (id) => {
    try {
      const raw = await services.deleteSupplier(id);
      const body = getAxiosBody<Result<unknown>>(raw);
      if (body?.code === 200) {
        set((state) => ({ suppliers: state.suppliers.filter((s) => s.id !== id) }));
        message.success('供应商删除成功');
      } else {
        message.error((body as { msg?: string })?.msg ?? '删除供应商失败');
      }
    } catch (error) {
      handleApiError(error, '删除供应商失败');
    }
  },
}));
